import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowUpDown, Loader, Users } from 'lucide-react'
import { useClientRegistries } from '@/hooks/useClientRegistries'
import { useEffect, useState, useMemo } from 'react'
import { getUserDataById } from '@/lib/userData'
import { Button } from '@/components/ui/button'
import { BookingSystemMenu } from '@/components/ui/menu/BookingSystemMenu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { DEMO_USERS } from '@/lib/demoBookingData'
import { useBookingSystem } from '@/contexts/BookingSystemContext'
import { doc, getDoc, Timestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { UserData } from '@/types/models'

interface ClientWithData {
  registryId: string
  userId: string
  userData: UserData | null
  tattooCount: number
  lastTattooDate: Date | null
  loading: boolean
  isDemoUser?: boolean
  lastBookingScheduledTime: Date | null
}

export type SortOption = 'recent'

/**
 * Generate demo client data using the same profiles as demo chat data
 * These clients have random tattoo counts and dates to simulate realistic data
 */
function generateDemoClients(): ClientWithData[] {
  const now = new Date()
  return DEMO_USERS.map((user, index) => {
    // Random tattoo count between 1-8
    const tattooCount = Math.floor(Math.random() * 8) + 1
    
    // Random last tattoo date within the last 6 months
    const daysAgo = Math.floor(Math.random() * 180)
    const lastTattooDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000)
    
    // Random booking scheduled time within the last 3 months
    const bookingDaysAgo = Math.floor(Math.random() * 90)
    const lastBookingScheduledTime = new Date(now.getTime() - bookingDaysAgo * 24 * 60 * 60 * 1000)
    
    return {
      registryId: `demo_registry_${index + 1}`,
      userId: user.uid,
      userData: {
        uid: user.uid,
        displayName: user.displayName,
        instagram: user.instagram,
        email: user.email,
        avatarUrl: user.avatarUrl,
      } as UserData,
      tattooCount,
      lastTattooDate,
      loading: false,
      isDemoUser: true,
      lastBookingScheduledTime,
    }
  })
}

export default function BookingSystemClientsPage() {
  const navigate = useNavigate()
  const { registries, loading: registriesLoading } = useClientRegistries()
  const { subscription } = useBookingSystem()
  const [clientsWithData, setClientsWithData] = useState<ClientWithData[]>([])
  const [loadingClients, setLoadingClients] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [sortBy, setSortBy] = useState<SortOption>('recent')
  const [sortByPopoverOpen, setSortByPopoverOpen] = useState(false)

  // Get includeFakeChats flag from subscription
  const includeFakeChats = subscription?.includeFakeChats ?? false

  // Fetch user data for each client registry
  useEffect(() => {
    const fetchClientsData = async () => {
      setLoadingClients(true)
      const clientsData: ClientWithData[] = []

      // Add demo clients only if includeFakeChats is enabled
      if (includeFakeChats) {
        const demoClients = generateDemoClients()
        clientsData.push(...demoClients)
      }

      // Then add real clients if any
      if (!registriesLoading && registries.length > 0) {
        for (const registry of registries) {
          try {
            const userData = await getUserDataById(registry.userId)
            
            // Fetch booking data if bookingId exists
            let lastBookingScheduledTime: Date | null = null
            if (registry.bookingId) {
              try {
                const bookingRef = doc(db, 'bookings', registry.bookingId)
                const bookingDoc = await getDoc(bookingRef)
                if (bookingDoc.exists()) {
                  const bookingData = bookingDoc.data()
                  if (bookingData.scheduledTimestamp) {
                    lastBookingScheduledTime = bookingData.scheduledTimestamp instanceof Timestamp
                      ? bookingData.scheduledTimestamp.toDate()
                      : new Date(bookingData.scheduledTimestamp)
                  }
                }
              } catch (error) {
                console.error(`Error fetching booking for registry ${registry.id}:`, error)
              }
            }
            
            clientsData.push({
              registryId: registry.id,
              userId: registry.userId,
              userData,
              tattooCount: registry.tattooCount,
              lastTattooDate: registry.lastTattooDate,
              loading: false,
              lastBookingScheduledTime,
            })
          } catch (error) {
            console.error(`Error fetching user data for ${registry.userId}:`, error)
            
            // Fetch booking data even if user data fails
            let lastBookingScheduledTime: Date | null = null
            if (registry.bookingId) {
              try {
                const bookingRef = doc(db, 'bookings', registry.bookingId)
                const bookingDoc = await getDoc(bookingRef)
                if (bookingDoc.exists()) {
                  const bookingData = bookingDoc.data()
                  if (bookingData.scheduledTimestamp) {
                    lastBookingScheduledTime = bookingData.scheduledTimestamp instanceof Timestamp
                      ? bookingData.scheduledTimestamp.toDate()
                      : new Date(bookingData.scheduledTimestamp)
                  }
                }
              } catch (error) {
                console.error(`Error fetching booking for registry ${registry.id}:`, error)
              }
            }
            
            clientsData.push({
              registryId: registry.id,
              userId: registry.userId,
              userData: null,
              tattooCount: registry.tattooCount,
              lastTattooDate: registry.lastTattooDate,
              loading: false,
              lastBookingScheduledTime,
            })
          }
        }
      }

      setClientsWithData(clientsData)
      setLoadingClients(false)
    }

    fetchClientsData()
  }, [registries, registriesLoading, includeFakeChats])

  const handleClientClick = (clientId: string, isDemoUser?: boolean) => {
    // Don't navigate if it's a demo user
    if (isDemoUser) {
      return
    }
    navigate(`/booking-system/clients/${clientId}`)
  }

  // Sort and filter clients
  const sortedAndFilteredClients = useMemo(() => {
    let filtered = clientsWithData

    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      filtered = filtered.filter(client => {
        return (
          client.userData?.instagram?.toLowerCase().includes(searchLower) ||
          client.userData?.displayName?.toLowerCase().includes(searchLower) ||
          client.userData?.email?.toLowerCase().includes(searchLower)
        )
      })
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'recent') {
        // Sort by most recent booking whose scheduled time just passed
        // Clients with booking times that just passed come first (most recent first)
        const now = new Date()
        
        // Prioritize real clients over demo users
        const aIsDemo = a.isDemoUser
        const bIsDemo = b.isDemoUser
        
        // Determine if a booking time has passed
        const aHasPassed = a.lastBookingScheduledTime && a.lastBookingScheduledTime <= now
        const bHasPassed = b.lastBookingScheduledTime && b.lastBookingScheduledTime <= now
        
        // If one is a real client with a past booking and the other is a demo user, real comes first
        if (aHasPassed && !aIsDemo && bIsDemo) {
          return -1
        } else if (bHasPassed && !bIsDemo && aIsDemo) {
          return 1
        }
        
        // If both have the same demo status
        if (aIsDemo === bIsDemo) {
          // If both have passed bookings or both don't have passed bookings
          if (aHasPassed && bHasPassed) {
            // Sort by most recent (highest timestamp first)
            return (b.lastBookingScheduledTime?.getTime() || 0) - (a.lastBookingScheduledTime?.getTime() || 0)
          } else if (aHasPassed) {
            // a has passed booking, b doesn't - a comes first
            return -1
          } else if (bHasPassed) {
            // b has passed booking, a doesn't - b comes first
            return 1
          } else if (a.lastBookingScheduledTime && b.lastBookingScheduledTime) {
            // Neither has passed yet, sort by most recent (highest timestamp first)
            return b.lastBookingScheduledTime.getTime() - a.lastBookingScheduledTime.getTime()
          } else if (a.lastBookingScheduledTime) {
            // Only a has a booking
            return -1
          } else if (b.lastBookingScheduledTime) {
            // Only b has a booking
            return 1
          } else {
            // Neither has a booking, maintain order
            return 0
          }
        }
        
        // If different demo status, real clients come first
        return aIsDemo ? 1 : -1
      }
      return 0
    })

    return sorted
  }, [clientsWithData, sortBy, searchQuery])


  const isLoading = registriesLoading || loadingClients

  return (
    <>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="phone-container-padded pt-4 pb-0">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="h-10 w-10 rounded-lg transition-all hover:shadow-lg bg-background dark:bg-card border border-border flex items-center justify-center"
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5 text-foreground" />
              </button>
              <h1 className="text-page-title text-foreground">
                Clients
              </h1>
            </div>
            <Button
              variant="ghost"
              className="!px-2"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
            >
              <Users className="h-6 w-6" />
            </Button>
          </div>
        </div>

      {/* Content */}
      <div className="phone-container-padded pb-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : clientsWithData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Users className="w-16 h-16 text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">No clients yet</h2>
            <p className="text-muted-foreground max-w-md">
              When users book appointments with you, they'll appear here.
            </p>
          </div>
        ) : (
          <>
            {/* Search bar */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 bg-muted/50 rounded-lg border border-border/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0 transition-colors"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Sort filter */}
            <div className="flex gap-3 mb-4">
              <Popover open={sortByPopoverOpen} onOpenChange={setSortByPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    className={`px-4 py-2 rounded-full text-xs font-medium transition-colors flex items-center gap-2 flex-shrink-0 ${
                      sortBy === 'recent'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <ArrowUpDown className="w-4 h-4" />
                    Recent
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-1" align="start">
                  <button
                    onClick={() => {
                      setSortBy('recent')
                      setSortByPopoverOpen(false)
                    }}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent ${
                      sortBy === 'recent'
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground'
                    }`}
                  >
                    Recent
                  </button>
                </PopoverContent>
              </Popover>
            </div>

            {/* Clients list */}
            {sortedAndFilteredClients.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No results found
                </h3>
                <p className="text-muted-foreground max-w-sm">
                  Try searching with a different term
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedAndFilteredClients.map((client) => (
                  <button
                    key={client.registryId}
                    onClick={() => handleClientClick(client.userId, client.isDemoUser)}
                    className="w-full bg-card rounded-lg hover:bg-muted/50 transition-colors text-left border border-input overflow-hidden"
                  >
                    <div className="grid grid-cols-[auto_1fr] h-20">
                      {/* Avatar - square, fills the side */}
                      <div className="relative">
                        <div className="w-20 h-20 overflow-hidden bg-primary/10 flex items-center justify-center">
                          {client.userData?.avatarUrl ? (
                            <img
                              src={client.userData.avatarUrl}
                              alt={client.userData.displayName || 'Client'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-foreground text-lg font-semibold">
                              {(client.userData?.displayName || client.userData?.email || 'C').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Client Info */}
                      <div className="min-w-0 flex flex-col justify-center py-1.5 pl-4 pr-4">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="font-semibold text-foreground truncate">
                            {client.userData?.instagram 
                              ? client.userData.instagram.replace(/^@/, '')
                              : client.userData?.displayName || client.userData?.email || 'Unknown Client'}
                          </h3>
                          {client.tattooCount > 0 && (
                            <span className="flex-shrink-0 text-xs font-medium px-2 py-1 bg-primary/10 text-primary rounded-full">
                              {client.tattooCount} {client.tattooCount === 1 ? 'tattoo' : 'tattoos'}
                            </span>
                          )}
                        </div>
                        
                        {/* Secondary email */}
                        {client.userData?.email && client.userData?.instagram && (
                          <p className="text-sm text-muted-foreground truncate">
                            {client.userData.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      </div>

      <BookingSystemMenu isOpen={menuOpen} onOpenChange={setMenuOpen} />
    </>
  )
}

